import { isDefined } from "@clipboard-health/util-ts";
import { MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS } from "@src/appV2/redesign/Shift/constants";
import { getShiftClockedInDurationInHours } from "@src/appV2/redesign/utils/getShiftClockedInDuration";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { type TimeRange } from "@src/appV2/Shifts/Shift/types";
import { isSolveUnpaidBreaksConfigEnabled } from "@src/appV2/Shifts/utils/isSolveUnpaidBreaksFeatureEnabled";

interface IsMandatoryBreakRequiredParams {
  requiresLunchBreak: boolean;
  solveUnpaidBreaksRolloutStageWithTargetingConfig: ShiftStateData["metadata"]["solveUnpaidBreaksRolloutStageWithTargetingConfig"];
  clockInTime?: Date;
  clockOutTime?: Date;
}

/**
 * Core function to determine if a mandatory break is required based on shift duration
 * Uses the facility's requirements and feature flag settings
 */
export function isMandatoryBreakRequired(params: IsMandatoryBreakRequiredParams): boolean {
  const {
    requiresLunchBreak,
    solveUnpaidBreaksRolloutStageWithTargetingConfig,
    clockInTime,
    clockOutTime,
  } = params;

  const isUnpaidBreaksFeatureEnabled =
    isSolveUnpaidBreaksConfigEnabled(solveUnpaidBreaksRolloutStageWithTargetingConfig) ?? false;

  const facilityRequiresMandatoryBreak = requiresLunchBreak ?? false;

  if (!isUnpaidBreaksFeatureEnabled || !facilityRequiresMandatoryBreak) {
    return false;
  }

  if (!isDefined(clockInTime) || !isDefined(clockOutTime)) {
    return false;
  }

  const clockInOut: TimeRange = {
    start: clockInTime.toISOString(),
    end: clockOutTime.toISOString(),
  };

  // not passing in lunchInOut on purpose
  // to calculate whether a mandatory break is required, we only look at the shift length
  const workedHours = getShiftClockedInDurationInHours(clockInOut);

  return (workedHours ?? 0) >= MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS;
}
